import React from "react";
import { Profile } from "@/hooks/use-search-suggestions";
import { useIsMobile } from "@/hooks/use-mobile";
import { VerifiedBadge } from "../designer-card/CardContent";

interface SearchSuggestionsProps {
  suggestions: Profile[];
  isLoading: boolean;
  searchValue: string;
  onSuggestionClick: (id: string) => void;
  suggestionsRef: React.RefObject<HTMLDivElement>;
}

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  suggestions,
  isLoading,
  searchValue,
  onSuggestionClick,
  suggestionsRef,
}) => {
  const isMobile = useIsMobile();

  const styles = isMobile
    ? {
        suggestions:
          "absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border-0 max-h-60 overflow-auto",
        suggestionItem:
          "flex items-center px-4 py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0",
      }
    : {
        suggestions:
          "absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-auto",
        suggestionItem:
          "px-4 py-3 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 flex items-center",
      };

  if (!searchValue.trim().length) {
    return null;
  }

  return (
    <div ref={suggestionsRef} className={styles.suggestions}>
      {isLoading ? (
        <div className="p-3 text-center text-sm text-gray-500">
          Loading suggestions...
        </div>
      ) : suggestions.length > 0 ? (
        <ul>
          {suggestions.map((profile) => (
            <li
              key={profile.id}
              className={styles.suggestionItem}
              onClick={() => onSuggestionClick(profile.username_slug)}
            >
              <div className="flex flex-col w-full">
                <div className="flex items-center gap-2 pl-4">
                  <span className="truncate">{profile.username || "Anonymous Designer"}</span>
                  {profile.is_featured && <VerifiedBadge height={14} width={14} className="mr-2" />}
                </div>
                {profile.profession && (
                  <div className="flex justify-start w-full pl-4">
                    <span className="text-xs text-gray-500 mt-0.5 truncate">
                      {profile.profession}
                    </span>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : searchValue.trim().length >= 2 ? (
        <div className="p-3 text-center text-sm text-gray-500">
          No matches found
        </div>
      ) : (
        <div className="p-3 text-center text-sm text-gray-500">
          Type at least 2 characters to search
        </div>
      )}
    </div>
  );
};

export default SearchSuggestions;
